import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import EditableField from "./EditableField";
import {
  Briefcase,
  MapPin,
  DollarSign,
  Calendar,
  Target,
  Send,
  Clock,
  Users,
  Trophy,
  XCircle,
  Pause,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Star,
} from "lucide-react";

const JobCard = ({
  job,
  isExpanded,
  onToggleExpanded,
  onUpdateJob,
  applicationStatuses,
}) => {
  const [saving, setSaving] = useState(false);

  const handleUpdate = useCallback(
    async (field, value) => {
      setSaving(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 300));
        onUpdateJob(job.id, { [field]: value });
      } finally {
        setSaving(false);
      }
    },
    [job.id, onUpdateJob],
  );

  const handleCompanyClick = () => {
    const url =
      job.companyWebsite ||
      `https://www.google.com/search?q=${encodeURIComponent(job.companyName)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getStatusConfig = (status) => {
    const configs = {
      "Not Applied": {
        color: "bg-gray-500",
        bgLight: "bg-gray-100",
        text: "text-gray-700",
        icon: Clock,
        label: "Not Applied",
      },
      Applied: {
        color: "bg-accent-electric",
        bgLight: "bg-accent-electric/20",
        text: "text-accent-electric",
        icon: Send,
        label: "Applied",
      },
      "Interview Scheduled": {
        color: "bg-accent-sunset",
        bgLight: "bg-accent-sunset/20",
        text: "text-accent-sunset",
        icon: Calendar,
        label: "Interview",
      },
      "Interview Completed": {
        color: "bg-primary-500",
        bgLight: "bg-primary-500/20",
        text: "text-primary-500",
        icon: Users,
        label: "Interviewed",
      },
      "Offer Received": {
        color: "bg-accent-neon",
        bgLight: "bg-accent-neon/20",
        text: "text-accent-neon",
        icon: Trophy,
        label: "Offer!",
      },
      Rejected: {
        color: "bg-red-500",
        bgLight: "bg-red-100",
        text: "text-red-600",
        icon: XCircle,
        label: "Rejected",
      },
      Withdrawn: {
        color: "bg-gray-500",
        bgLight: "bg-gray-100",
        text: "text-gray-700",
        icon: Pause,
        label: "Withdrawn",
      },
    };
    return configs[status] || configs["Not Applied"];
  };

  const statusConfig = getStatusConfig(job.applicationStatus);
  const StatusIcon = statusConfig.icon;

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDaysAgo = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getUrgencyColor = (deadline) => {
    if (!deadline) return null;
    const daysUntil = Math.ceil(
      (new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24),
    );

    if (daysUntil <= 3) return "text-red-500 bg-red-100";
    if (daysUntil <= 7) return "text-accent-sunset bg-accent-sunset/20";
    return "text-accent-neon bg-accent-neon/20";
  };

  const urgencyColor = getUrgencyColor(job.applicationDeadline);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="backdrop-blur-xl bg-light-card/80 border border-white/20 rounded-3xl shadow-lg hover:shadow-primary-500/30 transition-all duration-300 overflow-hidden group"
    >
      {/* Main Card Content */}
      <div className="p-6">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-xl font-black font-display text-gray-800 group-hover:text-primary-600 transition-colors duration-200">
                {job.jobTitle}
              </h3>
              {job.applicationStatus === "Offer Received" && (
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-accent-neon"
                >
                  <Star className="w-5 h-5" />
                </motion.div>
              )}
            </div>

            <div className="flex items-center gap-4 text-gray-600 mb-3">
              <div className="flex items-center gap-1">
                <Briefcase className="w-4 h-4" />
                <button
                  onClick={handleCompanyClick}
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200 flex items-center gap-1"
                  title="Visit company website"
                >
                  {job.companyName}
                  <ExternalLink className="w-3 h-3 ml-1" />
                </button>
              </div>

              {job.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">{job.location}</span>
                </div>
              )}

              {job.salaryRange && (
                <div className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm font-medium">{job.salaryRange}</span>
                </div>
              )}
            </div>

            {/* Status and Actions */}
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className={`px-4 py-2 rounded-full ${statusConfig.bgLight} ${statusConfig.text} font-bold text-sm flex items-center gap-2 cursor-pointer`}
              >
                <StatusIcon className="w-4 h-4" />
                {statusConfig.label}
              </motion.div>

              {job.jobUrl && (
                <motion.a
                  href={job.jobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  className="p-2 rounded-xl bg-white/40 border border-white/30 text-gray-600 hover:text-primary-600 transition-colors duration-200"
                >
                  <ExternalLink className="w-4 h-4" />
                </motion.a>
              )}
            </div>
          </div>

          {/* Expand/Collapse Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onToggleExpanded}
            className="p-2 rounded-xl bg-white/40 border border-white/30 text-gray-600 hover:text-primary-600 transition-all duration-200"
          >
            {isExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </motion.button>
        </div>

        {/* Tags and Meta */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {job.workType && (
            <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-bold">
              {job.workType}
            </span>
          )}

          {job.employmentType && (
            <span className="px-3 py-1 bg-accent-electric/20 text-accent-electric rounded-full text-xs font-bold">
              {job.employmentType}
            </span>
          )}

          {job.experienceLevel && job.experienceLevel !== "Not specified" && (
            <span className="px-3 py-1 bg-accent-sunset/20 text-accent-sunset rounded-full text-xs font-bold">
              {job.experienceLevel}
            </span>
          )}

          {urgencyColor && job.applicationDeadline && (
            <span
              className={`px-3 py-1 ${urgencyColor} rounded-full text-xs font-bold flex items-center gap-1`}
            >
              <Target className="w-3 h-3" />
              {formatDate(job.applicationDeadline)}
            </span>
          )}

          {job.dateApplied && (
            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
              Applied {getDaysAgo(job.dateApplied)}
            </span>
          )}
        </div>

        {/* Skills */}
        {job.skillsRequired && job.skillsRequired.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {job.skillsRequired.slice(0, 5).map((skill, index) => (
              <motion.span
                key={skill}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                className="px-3 py-1 bg-gradient-to-r from-primary-500/20 to-accent-hot/20 text-primary-700 rounded-full text-xs font-bold"
              >
                {skill}
              </motion.span>
            ))}
            {job.skillsRequired.length > 5 && (
              <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                +{job.skillsRequired.length - 5} more
              </span>
            )}
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="border-t border-white/20"
        >
          <div className="p-6 space-y-4">
            {/* Editable Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <EditableField
                  label="Application Status"
                  value={job.applicationStatus}
                  options={applicationStatuses}
                  onSave={(value) => handleUpdate("applicationStatus", value)}
                  type="select"
                  saving={saving}
                />
              </div>

              <div>
                <EditableField
                  label="Date Applied"
                  value={job.dateApplied}
                  onSave={(value) => handleUpdate("dateApplied", value)}
                  type="date"
                  saving={saving}
                />
              </div>

              <div>
                <EditableField
                  label="Application Deadline"
                  value={job.applicationDeadline}
                  onSave={(value) => handleUpdate("applicationDeadline", value)}
                  type="date"
                  saving={saving}
                />
              </div>

              <div>
                <EditableField
                  label="Priority"
                  value={job.priority || "Medium"}
                  options={["Low", "Medium", "High"]}
                  onSave={(value) => handleUpdate("priority", value)}
                  type="select"
                  saving={saving}
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <EditableField
                label="Notes"
                value={job.notes || ""}
                onSave={(value) => handleUpdate("notes", value)}
                type="textarea"
                saving={saving}
                placeholder="Add your notes about this application..."
              />
            </div>

            {/* Job Description */}
            {job.jobDescription && (
              <div>
                <label className="block text-sm font-bold font-display text-gray-700 mb-2">
                  Job Description
                </label>
                <div className="p-4 bg-white/40 backdrop-blur-sm border border-white/30 rounded-xl max-h-40 overflow-y-auto">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {job.jobDescription}
                  </p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default JobCard;
